import { NodeViewWrapper } from '@tiptap/react';
import { useState, useEffect, useCallback } from 'react';

export default function S3ImageComponent(props) {
    const { node, selected, editor } = props;
    const { src, alt, width, height, size, textAlign, uploadId, key } = node.attrs;
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(false);

    // Reset loaded state ONLY when the image source changes
    useEffect(() => {
        if (src) {
            setIsLoaded(false);
            setError(false);
        }
    }, [src, key]); 

    const handleLoad = useCallback(() => {
        setIsLoaded(true);
    }, []);

    const handleError = useCallback(() => {
        setIsLoaded(true); 
        setError(true);
    }, []);

    const sizes = {
        small: 'w-1/4',
        medium: 'w-1/2',
        large: 'w-3/4',
        full: 'w-full'
    };

    const alignments = {
        left: 'mr-auto',
        center: 'mx-auto',
        right: 'ml-auto'
    };

    const sizeClass = sizes[size || 'full'];
    const alignClass = alignments[textAlign || 'left'];
    const isUploading = !!uploadId;

    // Explicitly set aspect ratio to prevent layout shift and ensure skeleton height
    const hasDimensions = width && height;
    let style = {};

    if (hasDimensions) {
        style.aspectRatio = `${width} / ${height}`;
    } else if (!isLoaded && !error) {
        // Default skeleton shape only while loading if no dimensions known
        style.aspectRatio = "16 / 9";
    }
    // If loaded and no dimensions, no aspect-ratio style -> natural size

    const wrapperClasses = [
        's3-image-wrapper',
        'relative',
        'block',
        'mt-4',
        'mb-1',
        'rounded-lg',
        'transition-colors',
        'hover:bg-neutral-50/50 hover:dark:bg-neutral-900/50',
        selected ? '[.ProseMirror-focused_&]:bg-neutral-50 [.ProseMirror-focused_&]:dark:bg-neutral-900/50' : '',
    ].join(' ');

    const containerClasses = [
        's3-image-container',
        'block',
        sizeClass,
        alignClass,
        'bg-muted',
        'rounded-lg',
        // Only animate while loading
        (!isLoaded && !error) ? 'animate-pulse' : '',
    ].filter(Boolean).join(' ');

    const imageClasses = [
        'rounded-lg',
        'w-full',
        'h-auto', // Maintain aspect ratio based on width
        'transition-all',
        'transition-opacity',
        'duration-500',
        selected ? '[.ProseMirror-focused_&]:outline [.ProseMirror-focused_&]:outline-2 [.ProseMirror-focused_&]:outline-violet-500 [.ProseMirror-focused_&]:outline-offset-4' : '',
        isUploading ? 'animate-pulse' : '',
        // Opacity control - use opacity-0 to hide but keep layout space
        (isLoaded || error) ? 'opacity-100' : 'opacity-0',
    ].filter(Boolean).join(' ');

    return (
        <NodeViewWrapper className={wrapperClasses}>
            <div className={containerClasses}>
                <img
                    src={src}
                    alt={alt}
                    className={imageClasses}
                    style={style}
                    data-s3-key={key}
                    data-upload-id={uploadId}
                    data-width={width}
                    data-height={height}
                    data-size={size}
                    data-text-align={textAlign}
                    onLoad={handleLoad}
                    onError={handleError}
                />
            </div>
        </NodeViewWrapper>
    );
}
